import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import Pagination from './Pagination';
import ActivityChartModal from './ActivityChartModal';
import { formatTimeFromDate, formatNumericDate } from '../utils/timeFormat';
import './ActivitiesModal.css';

const ActivitiesModal = ({ isOpen, onClose, userId }) => {
  const { t, language } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_pages: 1,
    total_count: 0
  });
  const [hasMore, setHasMore] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);
  const scrollContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [events, setEvents] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  // Активность, для которой открыт график показателей
  const [chartActivity, setChartActivity] = useState(null);

  const fetchActivities = async (page = 1, append = false) => {
    if (loading || isLoadingMore) return;
    
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const perPage = isMobile ? 20 : 10;
      let url = `/api/users/${userId}/activities?page=${page}&per_page=${perPage}`;
      if (filterType) {
        url += `&activity_type=${filterType}`;
      }
      if (filterEvent) {
        url += `&event_id=${filterEvent}`;
      }
      
      // Добавляем параметры сортировки и фильтрации по дате
      url += `&sort=${sortOrder}`;
      
      if (dateFilter !== 'all') {
        url += `&date_filter=${dateFilter}`;
        if (dateFilter === 'custom') {
          if (customDateFrom) url += `&date_from=${customDateFrom}`;
          if (customDateTo) url += `&date_to=${customDateTo}`;
        }
      }

      const response = await axios.get(url);
      
      if (append && isMobile) {
        setActivities(prev => [...prev, ...response.data.activities]);
      } else {
        setActivities(response.data.activities);
      }
      
      setPagination(response.data.pagination);
      setHasMore(response.data.pagination.current_page < response.data.pagination.total_pages);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`/api/users/${userId}/activities/events_for_filter`);
      setEvents(response.data.events);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const syncActivities = async () => {
    setSyncing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post(`/api/users/${userId}/activities/sync`);
      
      // Сбрасываем и обновляем список активностей
      setActivities(response.data.activities);
      setPagination(response.data.pagination);
      setHasMore(response.data.pagination.current_page < response.data.pagination.total_pages);
      
      // Показываем сообщение об успехе
      const message = t('activities.syncResult')
        .replace('{synced}', response.data.synced_count)
        .replace('{total}', response.data.total_synced);
      setSuccessMessage(message);
      
      // Скрываем сообщение через 5 секунд
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      if (errorMsg.includes('No Rouvy integration')) {
        setError(t('activities.noIntegration'));
      } else if (errorMsg.includes('Access token expired')) {
        setError(t('activities.tokenExpired'));
      } else {
        setError(errorMsg);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Проверка на мобильное устройство
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setActivities([]);
      setHasMore(true);
      fetchActivities(1, false);
      fetchEvents();
    }
  }, [isOpen, filterType, filterEvent, isMobile, sortOrder, dateFilter, customDateFrom, customDateTo]);

  // Infinite scroll observer (только для мобильных)
  useEffect(() => {
    if (!isMobile || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchActivities(pagination.current_page + 1, true);
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, pagination.current_page, isMobile, fetchActivities]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchActivities(newPage);
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    
    const time = formatTimeFromDate(date, language);

    return `${formatNumericDate(date, language)}, ${time}`;
  };

  const formatDuration = (activity) => {
    const seconds = activity.moving_time
    const precize = activity.finish_time
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    let result = `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    if (precize !== null && precize !== undefined && activity.activity_type === 'race') {
      const fractional = precize % 1;
      const hundredths = Math.floor(fractional * 100);
      result += `.${hundredths.toString().padStart(2, '0')}`;
    }
    
    return result;
  };

  const formatDistance = (meters) => {
    if (!meters) return '-';
    return `${(meters / 1000).toFixed(2)} km`;
  };

  const formatSpeed = (metersPerSecond) => {
    if (!metersPerSecond) return '-';
    return `${(metersPerSecond * 3.6).toFixed(1)} km/h`;
  };

  // Оборудование активности: станок, пульсометр и остальные датчики.
  // Парсер (Fit::ActivityBuilder) раскладывает устройства по ролям и готовит
  // строку equipment.summary. Активности, разобранные старой версией парсера,
  // хранят сырые сообщения device_info — для них остаётся запасной разбор.
  const getEquipmentSummary = (activity) => {
    if (activity.fit_equipment_summary) return activity.fit_equipment_summary;

    const equipment = activity.fit?.equipment;
    if (equipment?.summary) return equipment.summary;

    if (Array.isArray(equipment?.sensors)) {
      const names = equipment.sensors.map(d => d.name).filter(Boolean);
      if (names.length) return names.join(' + ');
    }

    return getLegacyEquipmentSummary(activity);
  };

  const getLegacyEquipmentSummary = (activity) => {
    const devices = activity.fit?.device_info;
    if (!Array.isArray(devices)) return null;

    const names = devices
      .filter(d => d?.fields?.device_type?.value != null)
      .map(d => d.fields?.product_name?.value)
      .filter(Boolean);

    return names.length ? names.join(' + ') : null;
  };

  const getActivityTypeLabel = (type) => {
    const types = {
      race: t('activities.types.race'),
      activity: t('activities.types.activity'),
      workout: t('activities.types.workout'),
      groupRide: t('activities.types.groupRide')
    };
    return types[type] || type;
  };

  // Иконка графика показателей. Есть только у активностей с разобранным FIT —
  // без телеметрии строить график не по чему
  const renderChartButton = (activity) => {
    if (!activity.fit_parsed) return null;

    return (
      <button
        type="button"
        className="activity-chart-btn"
        title={t('activity_chart.open')}
        aria-label={t('activity_chart.open')}
        onClick={() => setChartActivity(activity)}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <polyline
            points="1.5,1.5 1.5,14.5 14.5,14.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="4,11 7,6.5 9.5,8.5 13.5,3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    );
  };

  const activityTypes = [
    { value: '', label: t('activities.types.all') },
    { value: 'race', label: t('activities.types.race') },
    { value: 'activity', label: t('activities.types.activity') },
    { value: 'workout', label: t('activities.types.workout') },
    { value: 'groupRide', label: t('activities.types.groupRide') }
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content activities-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('activities.title')}</h2>
          <div className="modal-header-actions">
            <button 
              className="btn-sync" 
              onClick={syncActivities}
              disabled={syncing || loading}
            >
              {syncing ? (
                <>
                  <span className="spinner">⟳</span>
                  {t('activities.syncing')}
                </>
              ) : (
                <>
                  <span>🔄</span>
                  {t('activities.refresh')}
                </>
              )}
            </button>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="modal-body">
          <div className="modal-body-header">
            {successMessage && (
              <div className="success-message">
                ✓ {successMessage}
              </div>
            )}
            
            {error && (
              <div className="error">{error}</div>
            )}
            
            <div className="filters-toggle-container">
              <button 
                className="filters-toggle-btn"
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              >
                <span className="filter-icon">🔍</span>
                {t('activities.filters')}
                <span className={`dropdown-arrow ${isFiltersOpen ? 'open' : ''}`}>▼</span>
              </button>
              
              {/* Показываем активные фильтры только если есть активные */}
              {(sortOrder === 'oldest' || dateFilter !== 'all' || filterType || filterEvent) && (
                <div className="active-filters">
                  {sortOrder === 'oldest' && (
                    <span className="filter-badge">
                      {t('activities.oldestFirst')}
                    </span>
                  )}
                  {dateFilter !== 'all' && (
                    <span className="filter-badge">
                      {dateFilter === 'today' && (t('activities.today'))}
                      {dateFilter === 'week' && (t('activities.days7'))}
                      {dateFilter === 'month' && (t('activities.days30'))}
                      {dateFilter === 'custom' && (t('activities.custom'))}
                    </span>
                  )}
                  {filterType && (
                    <span className="filter-badge">
                      {getActivityTypeLabel(filterType)}
                    </span>
                  )}
                  {filterEvent && (
                    <span className="filter-badge">
                      {events.find(e => e.id.toString() === filterEvent)?.name}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            <div className={`activities-filters ${isFiltersOpen ? 'open' : ''}`}>
              <div className="filters-group">
                <label>
                  {t('activities.type')}
                  <select 
                    value={filterType} 
                    onChange={(e) => setFilterType(e.target.value)}
                    className="filter-select"
                  >
                    {activityTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('activities.event')}
                  <select 
                    value={filterEvent} 
                    onChange={(e) => setFilterEvent(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">{t('activities.allEvents')}</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name} ({event.event_routes_count})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('activities.sort')}
                  <select 
                    value={sortOrder} 
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="filter-select"
                  >
                    <option value="newest">{t('activities.newestFirst')}</option>
                    <option value="oldest">{t('activities.oldestFirst')}</option>
                  </select>
                </label>
                <label>
                  {t('activities.period')}
                  <select 
                    value={dateFilter} 
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">{t('activities.allTime')}</option>
                    <option value="today">{t('activities.today')}</option>
                    <option value="week">{t('activities.last7')}</option>
                    <option value="month">{t('activities.last30')}</option>
                    <option value="custom">{t('activities.customPeriod')}</option>
                  </select>
                </label>
              </div>
              {dateFilter === 'custom' && (
                <div className="custom-date-range">
                  <label>
                    {t('activities.from')}
                    <input 
                      type="date" 
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="filter-select date-input"
                    />
                  </label>
                  <label>
                    {t('activities.to')}
                    <input 
                      type="date" 
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="filter-select date-input"
                    />
                  </label>
                </div>
              )}
              <div className="filters-actions">
                <button 
                  className="btn-reset-filters"
                  onClick={() => {
                    setFilterType('');
                    setFilterEvent('');
                    setSortOrder('newest');
                    setDateFilter('all');
                    setCustomDateFrom('');
                    setCustomDateTo('');
                  }}
                >
                  {t('activities.reset')}
                </button>
                <div className="activities-stats">
                  {t('activities.found')} {pagination.total_count}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-body-content" ref={scrollContainerRef}>
            {loading && activities.length === 0 ? (
              <div className="loading">{t('common.loading')}</div>
            ) : activities.length === 0 ? (
              <div className="no-activities">
                {t('activities.empty')}
              </div>
            ) : (
              <>
              {/* Desktop table view */}
              <div className="table-wrapper desktop-view">
                <table className="activities-table">
                  <thead>
                    <tr>
                      <th>{t('activities.colTitle')}</th>
                      <th>{t('activities.colDate')}</th>
                      <th>{t('activities.colDuration')}</th>
                      <th>{t('activities.colDistance')}</th>
                      <th>{t('activities.colPower')}</th>
                      <th>{t('activities.colHr')}</th>
                      <th>{t('activities.colSpeed')}</th>
                      <th>{t('activities.colCadence')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity) => (
                      <tr key={activity.id}>
                        <td className="activity-title">
                          <div className="activity-title-text">
                            {activity.activity_title ? (
                              <a
                                href={`https://riders.rouvy.com/activity/${activity.activity_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecoration: 'none' }}
                              >
                                <strong>{activity.activity_title}</strong>
                              </a>
                            ) : '-'}
                          </div>
                          {getEquipmentSummary(activity) && (
                            <div className="activity-trainer">
                              {t('activities.equipment')} {getEquipmentSummary(activity)}
                            </div>
                          )}
                          <div className="activity-type-row">
                            <div className={`activity-type-badge ${activity.activity_type}`}>
                              {getActivityTypeLabel(activity.activity_type)}
                            </div>
                            {renderChartButton(activity)}
                          </div>
                        </td>
                        <td>{formatDate(activity.start_date_utc)}</td>
                        <td 
                          style={{
                            color: activity.activity_type === 'race' && !activity.finish_time ? 'var(--c-8b0000-fg)' : 'var(--c-1f2937-fg)'
                          }}
                          title={activity.activity_type === 'race' && !activity.finish_time ? t('activities.dnfHint') : ''}
                        >
                          {formatDuration(activity)}
                        </td>
                        <td>{formatDistance(activity.distance)}</td>
                        <td>{activity.avg_power ? `${Math.round(activity.avg_power)} W` : '-'}</td>
                        <td>{activity.avg_heart_rate ? `${Math.round(activity.avg_heart_rate)} bpm` : '-'}</td>
                        <td>{activity.avg_speed ? formatSpeed(activity.avg_speed) : '-'}</td>
                        <td>{activity.avg_cadence ? `${Math.round(activity.avg_cadence)} rpm` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card view */}
              <div className="activities-cards mobile-view">
                <div className="activities-cards-grid">
                {activities.map((activity) => (
                  <div key={activity.id} className="activity-card">
                    <div className="activity-card-header">
                      <div className="activity-card-title-group">
                        <div className="activity-card-title">
                          {activity.activity_title ? (
                            <a
                              href={`https://riders.rouvy.com/activity/${activity.activity_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="activity-link"
                            >
                              {activity.activity_title}
                            </a>
                          ) : (
                            <span>{t('activities.untitled')}</span>
                          )}
                        </div>
                        {getEquipmentSummary(activity) && (
                          <div className="activity-trainer">
                            {t('activities.equipment')} {getEquipmentSummary(activity)}
                          </div>
                        )}
                      </div>
                      <div className={`activity-type-badge ${activity.activity_type}`}>
                        {getActivityTypeLabel(activity.activity_type)}
                      </div>
                    </div>
                    
                    <div className="activity-card-date">
                      📅 {formatDate(activity.start_date_utc)}
                      {renderChartButton(activity)}
                    </div>

                    <div className="activity-card-metrics">
                      <div className="metric-item">
                        <div className="metric-icon">⏱️</div>
                        <div className="metric-content">
                          <div className="metric-label">{t('activities.colDuration')}</div>
                          <div 
                            className="metric-value"
                            style={{
                              color: activity.activity_type === 'race' && !activity.finish_time ? 'var(--c-8b0000-fg)' : 'var(--c-1f2937-fg)'
                            }}
                          >
                            {formatDuration(activity)}
                            {activity.activity_type === 'race' && !activity.finish_time && (
                              <span className="dnf-badge">DNF</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="metric-item">
                        <div className="metric-icon">📏</div>
                        <div className="metric-content">
                          <div className="metric-label">{t('activities.colDistance')}</div>
                          <div className="metric-value">{formatDistance(activity.distance)}</div>
                        </div>
                      </div>

                      <div className="metric-item">
                        <div className="metric-icon">⚡</div>
                        <div className="metric-content">
                          <div className="metric-label">{t('activities.colPower')}</div>
                          <div className="metric-value">
                            {activity.avg_power ? `${Math.round(activity.avg_power)} W` : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="metric-item">
                        <div className="metric-icon">❤️</div>
                        <div className="metric-content">
                          <div className="metric-label">{t('activities.colHr')}</div>
                          <div className="metric-value">
                            {activity.avg_heart_rate ? `${Math.round(activity.avg_heart_rate)} bpm` : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="metric-item">
                        <div className="metric-icon">🚴</div>
                        <div className="metric-content">
                          <div className="metric-label">{t('activities.colSpeed')}</div>
                          <div className="metric-value">
                            {activity.avg_speed ? formatSpeed(activity.avg_speed) : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="metric-item">
                        <div className="metric-icon">🔄</div>
                        <div className="metric-content">
                          <div className="metric-label">{t('activities.colCadence')}</div>
                          <div className="metric-value">
                            {activity.avg_cadence ? `${Math.round(activity.avg_cadence)} rpm` : '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
                {isLoadingMore && (
                  <div className="loading-more">
                    <span className="spinner">⟳</span>
                    {t('common.loading')}
                  </div>
                )}
                <div ref={observerTarget} className="observer-target"></div>
              </div>
              </>
            )}
          </div>

          {/* Pagination для десктопа */}
          {!isMobile && !loading && activities.length > 0 && (
            <div className="modal-body-footer">
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                t={t}
                compact
              />
            </div>
          )}
        </div>

        <ActivityChartModal
          isOpen={!!chartActivity}
          onClose={() => setChartActivity(null)}
          userId={userId}
          activityId={chartActivity?.id}
          activityTitle={chartActivity?.activity_title || (t('activities.untitled'))}
        />
      </div>
    </div>
  );
};

export default ActivitiesModal;